import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import PlatformHubTabs from "../components/PlatformHubTabs";
import { latestMetricsByAccount, metricDisplayValue, usePlatformData } from "../hooks/usePlatformData";
import {
  ArrowUpRight, Camera, Clock3, DollarSign, ExternalLink, RefreshCw, Video,
} from "lucide-react";

const BREAKDOWN_KEYS = new Set([
  "faphouse_fanclub_monthly",
  "faphouse_fanclub_purchases_by_source",
  "faphouse_video_sales_by_video",
  "faphouse_video_sales_monthly",
  "faphouse_viewshare_daily",
  "faphouse_viewshare_monthly",
]);

function breakdownRows(metric) {
  if (Array.isArray(metric?.parsed_value)) return metric.parsed_value;
  try {
    const rows = JSON.parse(metric?.raw_value || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

function displayCell(value) {
  if (value === null || value === undefined || value === "") return "—";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

export default function FaphouseHub() {
  const { platforms, metrics, entities, syncRuns, loading, error, reload } = usePlatformData();
  const [selectedAccount, setSelectedAccount] = useState("all");
  const accountData = platforms.find((platform) => platform.id === "faphouse");
  const accounts = accountData?.accounts || [];
  const scopedMetrics = useMemo(() => latestMetricsByAccount(
    metrics.filter((metric) => metric.platform === "faphouse"
      && (selectedAccount === "all" || metric.account_id === selectedAccount)),
  ), [metrics, selectedAccount]);
  const scopedEntities = useMemo(() => entities.filter((entity) => entity.platform === "faphouse"
    && (selectedAccount === "all" || entity.account_id === selectedAccount)), [entities, selectedAccount]);
  const videos = scopedEntities.filter((entity) => entity.entity_type === "media_video")
    .sort((left, right) => String(right.last_seen || "").localeCompare(String(left.last_seen || "")));
  const payouts = scopedEntities.filter((entity) => entity.entity_type === "faphouse_payout")
    .sort((left, right) => String(right.last_seen || "").localeCompare(String(left.last_seen || "")));
  const financialMetrics = scopedMetrics.filter((metric) => metric.currency || metric.data_type === "currency");
  const breakdowns = scopedMetrics.filter((metric) => BREAKDOWN_KEYS.has(metric.metric_key)
    && breakdownRows(metric).length);
  const latestSync = syncRuns.filter((run) => run.platform === "faphouse"
    && (selectedAccount === "all" || run.account_id === selectedAccount))
    .sort((left, right) => String(right.started_at || "").localeCompare(String(left.started_at || "")))[0];
  const accountCount = selectedAccount === "all" ? accounts.length : accounts.filter((account) => account.account_id === selectedAccount).length;

  return (
    <AppLayout
      title="Faphouse Hub"
      overline="EXTENSION-SYNCED STUDIO DATA"
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            className="btn-ghost inline-flex items-center gap-2"
            title="Refresh saved extension captures">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh data
          </button>
          <a
            href="https://studio.faphouse.com/account/videos"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost inline-flex items-center gap-2 text-xs">
            <ExternalLink size={14} /> Open Studio
          </a>
        </div>
      }>
      <PlatformHubTabs platform="faphouse" platformName="Faphouse">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-zinc-800 pb-4">
        <div>
          <p className="text-sm text-zinc-400">Visible captures from your connected FapHouse Studio account.</p>
          <p className="mt-1 text-xs text-zinc-600">Last capture: {latestSync?.completed_at ? new Date(latestSync.completed_at).toLocaleString() : "No extension sync recorded"}</p>
        </div>
        <label className="min-w-48 text-xs text-zinc-500">Account
          <select aria-label="FapHouse account" value={selectedAccount} onChange={(event) => setSelectedAccount(event.target.value)} className="mt-1 min-h-10 w-full rounded border border-zinc-700 bg-zinc-950 px-3 text-sm text-zinc-200">
            <option value="all">All accounts</option>
            {accounts.map((account) => <option key={account.account_id} value={account.account_id}>{account.username || account.account_id}</option>)}
          </select>
        </label>
      </div>

      {error && <p role="alert" className="mb-4 rounded border border-red-900 bg-red-950/30 px-4 py-3 text-sm text-red-300">{error}</p>}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KPICard icon={Camera} label="Connected accounts" value={accountCount || "—"} color="#f59e0b" />
        <KPICard icon={Video} label="Captured videos" value={videos.length} color="#38bdf8" />
        <KPICard icon={Clock3} label="Captured payouts" value={payouts.length} color="#a78bfa" />
        <KPICard icon={DollarSign} label="Revenue observations" value={financialMetrics.length} color="#34d399" />
      </div>

      <section className="mb-6 border-y border-zinc-800 py-5" data-testid="faphouse-extension-earnings">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Earnings captured from FapHouse</h2>
            <p className="mt-1 text-xs text-zinc-500">Latest visible values by metric and account. These snapshots are not added together.</p>
          </div>
          <Link to="/crm/money?tab=observations" className="inline-flex items-center gap-1 text-sm text-emerald-300 hover:text-emerald-200">Open Platform Finance <ArrowUpRight size={14} /></Link>
        </div>
        {loading ? <p className="py-6 text-sm text-zinc-500">Loading extension captures…</p> : financialMetrics.length ? (
          <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2 xl:grid-cols-3">
            {financialMetrics.map((metric) => <div key={`${metric.account_id}:${metric.metric_key}`} className="flex min-w-0 justify-between gap-3 border-b border-zinc-800 py-3">
              <div className="min-w-0"><p className="break-words text-sm text-zinc-200">{metric.original_label || metric.metric_key}</p><p className="mt-1 text-xs text-zinc-600">{metric.account_id} · {metric.captured_at ? new Date(metric.captured_at).toLocaleString() : "Capture time unavailable"}</p></div>
              <span className="shrink-0 text-right text-sm font-semibold text-white">{metricDisplayValue(metric)}</span>
            </div>)}
          </div>
        ) : <EmptyState loading={loading} message="No earnings or payout values have been captured yet." />}
      </section>

      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <DataSection title="Video catalog" icon={Video}>
          {videos.length ? <div className="divide-y divide-zinc-800">{videos.slice(0, 12).map((video) => <article key={`${video.account_id}:${video.external_id}`} className="flex min-w-0 gap-3 py-3">
            {video.thumbnail_url && <img src={video.thumbnail_url} alt="" className="h-14 w-20 shrink-0 object-cover" />}
            <div className="min-w-0 flex-1"><p className="break-words text-sm font-medium text-white">{video.title_or_label || "Untitled video"}</p><p className="mt-1 text-xs text-zinc-500">{video.account_id} · {video.status || "Status unavailable"}{video.publish_date ? ` · ${video.publish_date}` : ""}</p>{video.entity_url && <a href={video.entity_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-violet-300">Open listing <ExternalLink size={11} /></a>}</div>
          </article>)}</div> : <EmptyState loading={loading} message="Sync the Studio Videos page from the extension to see video cards here." />}
        </DataSection>
        <DataSection title="Payout history" icon={DollarSign}>
          {payouts.length ? <div className="divide-y divide-zinc-800">{payouts.slice(0, 12).map((payout) => <article key={`${payout.account_id}:${payout.external_id}`} className="flex justify-between gap-3 py-3">
            <div className="min-w-0"><p className="text-sm font-medium text-white">{payout.normalized?.payout_date || payout.title_or_label || "Payout"}</p><p className="mt-1 text-xs text-zinc-500">{payout.account_id} · {payout.normalized?.payout_method || "Method unavailable"} · {payout.status || "Status unavailable"}</p></div>
            <span className="shrink-0 text-sm font-semibold text-white">{payout.price === null || payout.price === undefined ? payout.normalized?.payout_amount || "—" : `${payout.currency || ""} ${payout.price}`}</span>
          </article>)}</div> : <EmptyState loading={loading} message="Sync the Studio Payouts page from the extension to see payout rows here." />}
        </DataSection>
      </div>

      <section className="border-t border-zinc-800 pt-5" data-testid="faphouse-extension-breakdowns">
        <h2 className="mb-1 text-lg font-semibold text-white">Captured earnings breakdowns</h2>
        <p className="mb-4 text-xs text-zinc-500">Monthly and daily rows are shown as captured from the selected Studio reports.</p>
        {breakdowns.length ? <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">{breakdowns.map((metric) => <Breakdown key={`${metric.account_id}:${metric.metric_key}`} metric={metric} />)}</div> : <EmptyState loading={loading} message="Sync View-share, Fanclubs, or Video Sales to capture report breakdowns." />}
      </section>

      {!accounts.length && !loading && <p className="mt-6 border-t border-zinc-800 pt-4 text-sm text-zinc-500">No FapHouse account is connected. <Link to="/crm/platform-hub" className="text-violet-300 hover:text-violet-200">Connect the extension in Platform Matrix</Link>.</p>}
      </PlatformHubTabs>
    </AppLayout>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  return <div className="border-b border-zinc-800 py-3"><div className="flex items-center gap-2 text-xs uppercase text-zinc-500"><Icon size={15} style={{ color }} />{label}</div><div className="mt-2 text-2xl font-semibold text-white">{value}</div></div>;
}

function DataSection({ title, icon: Icon, children }) {
  return <section className="min-w-0 border-t border-zinc-800 pt-4"><h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-zinc-200"><Icon size={15} className="text-amber-300" />{title}</h2>{children}</section>;
}

function EmptyState({ loading, message }) {
  return <p className="py-6 text-sm text-zinc-500">{loading ? "Loading extension captures…" : message}</p>;
}

function Breakdown({ metric }) {
  const rows = breakdownRows(metric);
  const columns = Array.from(new Set(rows.flatMap((row) => Object.keys(row || {}))));
  return <div className="min-w-0 border-t border-zinc-800 pt-3">
    <div className="mb-2 flex flex-wrap justify-between gap-2"><div><h3 className="text-sm font-medium text-zinc-200">{metric.original_label || metric.metric_key}</h3><p className="text-xs text-zinc-600">{metric.account_id} · {metric.captured_at ? new Date(metric.captured_at).toLocaleString() : "Capture time unavailable"}</p></div>{metric.source_url && <a href={metric.source_url} target="_blank" rel="noreferrer" aria-label={`Open source for ${metric.original_label || metric.metric_key}`} className="inline-flex h-8 w-8 items-center justify-center text-violet-300" title="Open source"><ExternalLink size={14} /></a>}</div>
    <div className="overflow-x-auto"><table className="w-full min-w-[400px] text-left text-xs"><thead><tr className="border-b border-zinc-800 text-zinc-500">{columns.map((column) => <th key={column} className="px-2 py-2 font-medium">{column.replace(/_/g, " ")}</th>)}</tr></thead><tbody>{rows.slice(0, 12).map((row, index) => <tr key={`${metric.metric_key}:${index}`} className="border-b border-zinc-900 text-zinc-300">{columns.map((column) => <td key={column} className="px-2 py-2">{displayCell(row[column])}</td>)}</tr>)}</tbody></table></div>
  </div>;
}
